import React, { useState } from 'react';
import classNames from 'classnames';
import { Col, Row, FormCheck, FormGroup, FormLabel, FormControl, Button } from 'react-bootstrap';
import PdfObject from '../PdfObject';
import ApiService from '../../services/ApiService';
import { modal } from '../modal/ModalEmitter';
import toastr from '../toastr';
import { isValidEmail } from '../../util/general';
import '../../styles/pdfwrapper.scss';

const PdfWrapper = ({ url = '', initialRecipients = [], initialSubject = '', initialMessage = '' }) => {
  const [message, setMessage] = useState(initialMessage);
  const [subject, setSubject] = useState(initialSubject);
  const [recipients, setRecipients] = useState(initialRecipients);
  const [addingEmail, setAddingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [closeOnSend, setCloseOnSend] = useState(true);

  const handleSubmit = () => {
    const submit = () => {
      setSending(true);

      const payload = {
        documentPath: url,
        subject,
        recipients,
        message,
      };

      return ApiService.post('/document/email', payload).then(() => {
        toastr.success(
          'Document Sent',
          `${subject || 'No Subject'} to ${recipients.length} recipient${recipients.length > 1 ? 's' : ''}`
        );
        setSending(false);

        if (closeOnSend) {
          modal.close();
        }
      });
    };

    if (!subject || !message) {
      const warning = message ? 'subject' : `message${!subject ? ' and subject' : ''}`;
      modal.confirm({
        message: <span>Missing {warning}. Submit anyway?</span>,
        callback: submit,
      });
      return;
    }

    submit();
  };

  const handleEmailSelect = email => {
    if (recipients.includes(email.toLowerCase())) {
      setRecipients(current => current.filter(r => r !== email));
      return;
    }

    addRecipient(email);
  };

  const addRecipient = email => {
    const newEmail = email.toLowerCase().trim();

    if (!newEmail) {
      return;
    }

    if (!isValidEmail(newEmail)) {
      return;
    }

    if (recipients.includes(newEmail)) {
      return;
    }

    setRecipients(current => [...current, newEmail]);
  };

  const handleNewEmailKeyDown = event => {
    if (event.key === 'Enter') {
      submitNewEmail();
    }
  };

  const submitNewEmail = () => {
    let bail = false;

    [';', ',', ' '].forEach(delimiter => {
      const delimtest = new RegExp(delimiter);
      if (delimtest.test(newEmail)) {
        const entries = newEmail.split(delimiter);
        entries.forEach(addRecipient);
        bail = true;
      }
    });

    if (bail) {
      setNewEmail('');
      setAddingEmail(false);
      return;
    }

    addRecipient(newEmail);
    setNewEmail('');
    setAddingEmail(false);
  };

  const handleAutoClose = event => {
    setCloseOnSend(event.target.checked);
  };

  const renderRecipients = () => {
    return (
      <div className="email-recipients">
        {recipients.map(r => {
          const checked = recipients.includes(r);
          return (
            <div
              className={classNames('email-recipient', { selected: checked })}
              key={r}
              onClick={() => handleEmailSelect(r)}
            >
              <FormCheck>
                <FormCheck.Input type="checkbox" checked={checked} readOnly />
                <FormCheck.Label>{r}</FormCheck.Label>
              </FormCheck>
            </div>
          );
        })}

        {addingEmail === true ? (
          <FormGroup>
            <FormControl
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={handleNewEmailKeyDown}
              placeholder="Enter Recipient"
              autoFocus
            />
            <Row>
              <Col md={6} style={{ paddingRight: '7px' }}>
                <Button
                  className="mb-0 mt-2"
                  variant="secondary"
                  size="sm"
                  style={{ width: '100%', color: 'var(--green-5)' }}
                  onClick={() => submitNewEmail()}
                >
                  <i className="fas fa-check" />
                </Button>
              </Col>
              <Col md={6} style={{ paddingLeft: '7px' }}>
                <Button
                  className="mb-0 mt-2"
                  variant="secondary"
                  size="sm"
                  style={{ width: '100%', color: 'var(--red-5)' }}
                  onClick={() => setAddingEmail(false)}
                >
                  <i className="fas fa-times" />
                </Button>
              </Col>
            </Row>
          </FormGroup>
        ) : (
          <Button className="mb-2" variant="secondary" block size="sm" onClick={() => setAddingEmail(true)}>
            <i className="fas fa-plus" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="pdf-wrapper">
      <Row>
        <Col md={8} lg={8} className="pdf-document">
          <PdfObject url={url} width="100%" height="800" />,
        </Col>
        <Col md={4} lg={4} className="pdf-controls">
          <div className="email-controls mb-2">
            <Button variant="primary" block onClick={handleSubmit} disabled={sending || !recipients.length}>
              {sending ? (
                <>
                  <i className="fad fa-spinner-third fa-spin" /> Sending
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane" /> Send
                </>
              )}
            </Button>
          </div>
          <div className="section-heading">Choose Recipients</div>
          {renderRecipients()}
          <div className="section-heading">Compose</div>
          <FormGroup>
            <FormLabel>Subject</FormLabel>
            <FormControl value={subject} onChange={e => setSubject(e.target.value)} />
          </FormGroup>

          <FormGroup className={classNames('form-textarea', { 'is-empty': message === '' })}>
            <FormLabel>Message</FormLabel>
            <FormControl as="textarea" value={message} onChange={e => setMessage(e.target.value)} rows={5} />
          </FormGroup>

          <FormCheck>
            <FormCheck.Input type="checkbox" id="pdf-cos" onChange={handleAutoClose} checked={closeOnSend} />
            <FormCheck.Label htmlFor="pdf-cos">Close This On Send</FormCheck.Label>
          </FormCheck>
        </Col>
      </Row>
    </div>
  );
};

export default PdfWrapper;
